/**
 * Explore Page
 * Constellation-based music discovery interface
 */

import { useEffect } from 'react';
import Masonry from 'react-masonry-css';
import { useExploreStore } from '../stores/explore-store';
import { usePlaylistExportStore } from '../stores/playlist-export.store';
import { getSavedConstellations } from '../services/explore.service';
import ConstellationBuilder from '../components/explore/ConstellationBuilder';
import VibeField from '../components/explore/VibeField';
import ExploreActions from '../components/explore/ExploreActions';
import CoherenceMeter from '../components/explore/CoherenceMeter';
import TrackCard from '../components/TrackCard';
import ExploreEmptyState from '../components/explore/ExploreEmptyState';
import ExportPlaylistModal from '../components/ExportPlaylistModal';

export default function Explore() {
  const {
    nodes,
    results,
    interpretation,
    loading,
    error,
    coherenceScore,
    cacheHit,
    responseTimeMs,
    setSavedConstellations,
    addNode,
  } = useExploreStore();

  const { openExportModal } = usePlaylistExportStore();

  // Load saved constellations on mount
  useEffect(() => {
    loadSavedConstellations();
  }, []);

  const loadSavedConstellations = async () => {
    try {
      const constellations = await getSavedConstellations();
      setSavedConstellations(constellations);
    } catch (err) {
      console.error('Failed to load saved constellations:', err);
    }
  };

  const handleAddAsNode = (artist: string) => {
    addNode({
      type: 'artist',
      value: artist,
      weight: 0.5,
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Explore</h1>
          <p className="text-sm sm:text-base text-gray-600">
            Map your influences. Find what connects them.
          </p>
        </div>

        {/* Main Content - Fixed Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Sidebar - Fixed */}
          <div className="lg:col-span-5 space-y-6">
            {/* Constellation Builder */}
            <ConstellationBuilder />

            {/* Vibe Field */}
            <VibeField />

            {/* Action Buttons */}
            <ExploreActions />

            {/* Coherence Meter (if available) */}
            {results.length > 0 && coherenceScore > 0 && (
              <CoherenceMeter score={coherenceScore} />
            )}
          </div>

          {/* Right Content - Results */}
          <div className="lg:col-span-7">
            {loading ? (
              // Loading Animation
              <div className="flex flex-col items-center justify-center py-20">
                <div className="relative w-32 h-32 mb-8">
                  {/* Constellation animation */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-24 h-24 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin animation-delay-150" style={{ animationDirection: 'reverse' }} />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Chasing a thread...
                </h3>
                <p className="text-gray-600 text-center max-w-md">
                  Following the signal through {nodes.length} influences
                </p>
                <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
                  <span className="inline-block w-2 h-2 bg-blue-600 rounded-full animate-pulse" />
                  <span>Give us a beat.</span>
                </div>
              </div>
            ) : error ? (
              // Error State
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <h3 className="text-red-800 font-bold mb-2 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  Error
                </h3>
                <p className="text-red-700">{error}</p>
              </div>
            ) : results.length > 0 ? (
              // Results
              <div className="space-y-6">
                {/* Interpretation Box */}
                {interpretation && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-blue-900 mb-1">
                          Where we went with this:
                        </h3>
                        <p className="text-sm text-blue-800">{interpretation}</p>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-blue-700 flex-shrink-0">
                        {cacheHit && (
                          <span className="px-2 py-1 bg-blue-100 rounded whitespace-nowrap">
                            Cached
                          </span>
                        )}
                        <span className="px-2 py-1 bg-blue-100 rounded whitespace-nowrap">
                          {(responseTimeMs / 1000).toFixed(1)}s
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Results Header */}
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {results.length} Tracks
                  </h2>
                  <button
                    onClick={() => openExportModal(results)}
                    className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Export to Playlist
                  </button>
                </div>

                {/* Masonry Grid */}
                <Masonry
                  breakpointCols={{
                    default: 2,
                    1024: 1,
                  }}
                  className="flex -ml-4 w-auto"
                  columnClassName="pl-4 bg-clip-padding"
                >
                  {results.map((track) => (
                    <div key={track.id} className="mb-4">
                      <TrackCard track={track} onAddAsNode={handleAddAsNode} />
                    </div>
                  ))}
                </Masonry>
              </div>
            ) : nodes.length === 0 ? (
              // Empty State - No constellation built
              <ExploreEmptyState />
            ) : (
              // Empty State - Constellation built but no results
              <div className="flex flex-col items-center justify-center py-20">
                <span className="text-6xl mb-4">🌌</span>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Constellation's set.
                </h3>
                <p className="text-gray-600 text-center max-w-md">
                  {nodes.length} node{nodes.length !== 1 ? 's' : ''} locked in. Hit Generate when you're ready.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <ExportPlaylistModal />
    </div>
  );
}
